import { ContentGroupDetailHeader } from './ContentGroupDetailHeader';
import { ContentGroupDetailSidebar } from './ContentGroupDetailSidebar';
import { getContentGroup } from '@/api';
import { Device } from '@/classes';
import { ContentCard } from '@/entities/content';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { Content, ContentGroupDetail as ContentGroupDetailType } from '@/types';
import { scrollElementTo } from '@/utils';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ContentGroupDetailProps = {
  id: number;
  sourcePage: string;
};

export const ContentGroupDetail = ({ id, sourcePage }: ContentGroupDetailProps) => {
  const [searchParams] = useSearchParams();
  // ! focusable: false — 컨테이너 자신은 절대 포커스 대상이 되지 않게 한다. (isContainer 주석 참고)
  const { ref } = useExtendFocusable({
    isContainer: true,
    saveLastFocusedChild: true,
    trackChildren: true,
    autoRestoreFocus: true,
    isFocusBoundary: true,
    focusable: false,
  });

  const [loading, setLoading] = useState(false);
  const [contentGroup, setContentGroup] = useState<ContentGroupDetailType>();
  const [contents, setContents] = useState<Array<Content>>([]);
  const loadContentGroup = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const contentGroup = await getContentGroup({
        id,
        contentType: 'VIDEO',
        headerType: 'default',
        language: Device.language,
        resolution: 'P33',
      });

      setContentGroup(contentGroup);
      setContents(contentGroup.contents);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load content groups', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onFocused = useCallback(
    ({ y }: { y: number }) => {
      const current = ref.current;
      if (current) {
        scrollElementTo({ element: current, top: y < 400 ? 0 : y - 170 });
      }
    },
    [ref],
  );

  const skeletonComponents = useMemo(
    () =>
      new Array(12)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className="w-[533px] h-[300px] mr-[32px] mb-[32px] relative overflow-hidden ease-in-out duration-300 rounded-[20px] outline-disable bg-gray-400 animate-pulse"
          />
        )),
    [],
  );

  const components = useMemo(() => {
    if (contents) {
      return contents
        .filter((content) => content.type === 'VIDEO')
        .map((content, i) => {
          const focusKey = `content-${i}`;
          return (
            <ContentCard
              sourcePage={sourcePage}
              tabIndex={i}
              key={i}
              content={content}
              focusKey={focusKey}
              onFocused={onFocused}
              weight={i}
              topicId={contentGroup?.id}
            />
          );
        });
    }

    return [];
  }, [contents, onFocused, sourcePage, contentGroup?.id]);

  useEffect(() => {
    loadContentGroup(id);
  }, [id, loadContentGroup]);

  useEffect(() => {
    const focusKey = searchParams.get('f');
    if (!focusKey) {
      if (contentGroup) {
        setFocus('content-0');
      }
    }
  }, [contentGroup, searchParams]);

  return (
    <main
      id="main-container"
      className="w-full h-screen overflow-x-hidden overflow-y-auto bg-[#F1FDFF] relative"
      ref={ref}
    >
      {/* Content Group Header */}
      <ContentGroupDetailHeader contentGroup={contentGroup} />

      {/* Content Group Detail */}
      <div className="flex pt-[180px]">
        {/* Sidebar  */}
        <ContentGroupDetailSidebar scrollingRef={ref} targetFocusKey="content-0" />

        <div className="flex flex-col ml-[169px]">
          <div className="flex flex-row flex-wrap items-center w-[1747px] mt-[24px] mb-[4px] pl-[12px]">
            {/* Skeleton */}
            {components.length === 0 && loading && skeletonComponents}

            {/* ContentList */}
            {components.length > 0 && components}
          </div>
        </div>
      </div>
    </main>
  );
};
